import { useState } from 'react';
import { FORM_ERROR, getDRFFormError, type SubmissionErrors } from '@ff-it/form';
import type { RequestArgs, RequestFailure } from '@ff-it/api';
import useSWR from 'swr';
import { api } from 'services';
import { formatError } from 'utilities';
import type { ExportPreset } from '../columns';

export function useExportPresets(exportKey: string) {
  const url = `core/export-presets/${encodeURIComponent(exportKey)}/`;
  const { data: presets, error, mutate } = useSWR<ExportPreset[]>({ method: 'GET', url });
  const [busy, setBusy] = useState(false);

  async function request<T>(args: RequestArgs, onSuccess: (data: T) => void): Promise<SubmissionErrors | void> {
    setBusy(true);
    try {
      const result = await api.request<T, unknown>(args);
      if (!result.ok) throw result;
      await mutate();
      onSuccess(result.data);
    } catch (error) {
      return getDRFFormError(error as RequestFailure<unknown>) ?? { [FORM_ERROR]: formatError(error) };
    } finally {
      setBusy(false);
    }
  }

  return {
    presets,
    error,
    busy,
    save: (
      body: Partial<Pick<ExportPreset, 'name' | 'columns' | 'is_default'>>,
      onSuccess: (preset: ExportPreset) => void,
      id?: number | null,
    ) =>
      request<ExportPreset>(
        { method: id == null ? 'POST' : 'PATCH', url: id == null ? url : `${url}${id}/`, body },
        onSuccess,
      ),
    remove: (id: number, onSuccess: () => void) => request<void>({ method: 'DELETE', url: `${url}${id}/` }, onSuccess),
  };
}
